import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Company, CompanyInput } from '@pirouette/shared';
import { companyInputSchema } from '@pirouette/shared';
import { stripUndefined } from '@/lib/db';
import { supabase } from '@/lib/supabase';

export type { Company, CompanyInput } from '@pirouette/shared';

const queryKey = ['companies'];

export interface CompanyListQuery {
  search?: string | undefined;
  season?: string | undefined;
  active?: boolean | undefined;
}

export function useCompanies(query: CompanyListQuery = {}) {
  return useQuery({
    queryKey: [...queryKey, query],
    queryFn: async (): Promise<Company[]> => {
      let q = supabase
        .from('companies')
        .select('*')
        .is('deleted_at', null)
        .order('season', { ascending: false })
        .order('name', { ascending: true });
      if (query.search?.trim()) {
        const term = `%${query.search.trim()}%`;
        q = q.or(`name.ilike.${term},label.ilike.${term},notes.ilike.${term}`);
      }
      if (query.season?.trim()) q = q.eq('season', query.season.trim());
      if (query.active !== undefined) q = q.eq('active', query.active);

      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: CompanyInput): Promise<Company> => {
      const parsed = companyInputSchema.parse(input);
      const { data, error } = await supabase
        .from('companies')
        .insert(stripUndefined(parsed))
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (company) => {
      qc.setQueriesData<Company[]>({ queryKey }, (current) => [...(current ?? []), company]);
    },
  });
}
